import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

/* ─────────────────────────────────────────────
   TYPES
───────────────────────────────────────────── */

export interface CommerceCartItem {
  listingId: string;
  name: string;
  price: number;
  qty: number;
}

interface CommerceServiceFeeState {
  items: CommerceCartItem[];

  subtotal: number;
  totalQty: number;

  guestsFee: number;
  hostsFee: number;
  hostProfit: number;
  platformProfit: number;
  totalCharged: number;

  calculated: boolean;
}

/* ─────────────────────────────────────────────
   UTILITY FUNCTIONS (IDENTICAL)
───────────────────────────────────────────── */

export const calculateHostEarnings = (total: number) => {
  const hostFeePercentage = 0.20; // no fee to host initially
  const hostsFee = total * hostFeePercentage;
  const hostProfit = parseFloat((total - hostsFee).toFixed(2));
  return { hostProfit, hostsFee };
};

export const calculateGuestServiceFee = (subtotal: number): number => {
  let guestsFee: number;
  if (subtotal > 5000) guestsFee = subtotal * 0.2;
  else if (subtotal > 1000) guestsFee = subtotal * 0.2;
  else if (subtotal > 500) guestsFee = subtotal * 0.12;
  else if (subtotal > 100) guestsFee = subtotal * 0.1;
  else guestsFee = subtotal * 0.08;
  return parseFloat(guestsFee.toFixed(2));
};

export const calculatePlatformProfit = (
  hostsFee: number,
  guestsFee: number
): number =>
  parseFloat((hostsFee + guestsFee).toFixed(2));

/* ─────────────────────────────────────────────
   ASYNC CALCULATION THUNK
───────────────────────────────────────────── */

export const calculateCommerceEarnings = createAsyncThunk(
  "commerceServiceFees/calculateCommerceEarnings",
  async ({ items }: { items: CommerceCartItem[] }) => {
    const totalQty = items.reduce((s, i) => s + i.qty, 0);

    const subtotal = items.reduce(
      (s, i) => s + i.qty * i.price,
      0
    );

    // 🔥 SAME FLOW AS FASHION
    const { hostProfit, hostsFee } = calculateHostEarnings(subtotal);
    const guestsFee = calculateGuestServiceFee(subtotal);
    const platformProfit = calculatePlatformProfit(
      hostsFee,
      guestsFee
    );

    const totalCharged = parseFloat(
      (subtotal + guestsFee).toFixed(2)
    );

    return {
      items,
      totalQty,
      subtotal,

      guestsFee,
      hostsFee,
      hostProfit,
      platformProfit,
      totalCharged,
    };
  }
);

/* ─────────────────────────────────────────────
   SLICE
───────────────────────────────────────────── */

const initialState: CommerceServiceFeeState = {
  items: [],
  subtotal: 0,
  totalQty: 0,

  guestsFee: 0,
  hostsFee: 0,
  hostProfit: 0,
  platformProfit: 0,
  totalCharged: 0,

  calculated: false,
};

const commerceServiceFeeSlice = createSlice({
  name: "commerceServiceFees",
  initialState,
  reducers: {
    resetCommerceServiceFees: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(
      calculateCommerceEarnings.fulfilled,
      (state, action) => {
        state.items = action.payload.items;
        state.totalQty = action.payload.totalQty;
        state.subtotal = action.payload.subtotal;

        state.guestsFee = action.payload.guestsFee;
        state.hostsFee = action.payload.hostsFee;
        state.hostProfit = action.payload.hostProfit;
        state.platformProfit = action.payload.platformProfit;
        state.totalCharged = action.payload.totalCharged;

        state.calculated = true;
      }
    );
  },
});

export const { resetCommerceServiceFees } =
  commerceServiceFeeSlice.actions;

export default commerceServiceFeeSlice.reducer;
